// ⚠️ If you modify the styling of this component and you're using the SectionListings component in your marketplace (featured listings)
// please reflect those changes in the calculateCarouselHeight function in SectionListing.js to avoid layout issues
import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { useIntl } from '../../util/reactIntl';
import { requireListingImage } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { createSlug } from '../../util/urlHelpers';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
} from '../../components';

import { getListingCardTranslations } from './ListingCard.helpers';

import css from './ListingCard.module.css';
import { currentUserTypeSelector } from '../../ducks/user.duck';
import { useSelector } from 'react-redux';

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const mapEnumToLabel = (enumOptions, value) =>
  enumOptions?.find(o => o.option === value)?.label || value;

const getCategoryName = (categories, categoryId) => {
  const cat = categories?.find(c => c.id === categoryId);
  return cat?.name || null;
};

const getFreelancerDetailLines = (listing, listingFieldConfigs, config) => {
  const publicData = listing?.attributes?.publicData || {};
  const categoryLevel1 = publicData?.categoryLevel1;
  const categories = config?.categoryConfiguration?.categories || [];

  if (categoryLevel1 === 'content-creator') {
    const lines = [];

    const categoryLevel2 = publicData?.categoryLevel2;
    const categoryName = categoryLevel2
      ? getCategoryName(
          categories.flatMap(c => c.subcategories || []),
          categoryLevel2
        )
      : null;
    if (categoryName) lines.push(categoryName);

    const socialPlatforms = publicData?.social_platforms;
    if (Array.isArray(socialPlatforms)) {
      const socialConfig = listingFieldConfigs.find(cfg => cfg?.key === 'social_platforms');
      const socialOptions = socialConfig?.enumOptions || [];
      socialPlatforms.forEach(platformKey => {
        const label = mapEnumToLabel(socialOptions, platformKey);
        const count = publicData[`followers_${platformKey}`];
        lines.push(
          count != null ? `${label} \u2013 ${Number(count).toLocaleString()} followers` : label
        );
      });
    }

    return lines.filter(Boolean);
  }

  if (categoryLevel1 === 'ai-creator') {
    const aiSkills = publicData?.ai_skills;
    if (!Array.isArray(aiSkills) || aiSkills.length === 0) return [];

    const aiSkillsConfig = listingFieldConfigs.find(cfg => cfg?.key === 'ai_skills');
    const aiSkillOptions = aiSkillsConfig?.enumOptions || [];
    return aiSkills.map(v => mapEnumToLabel(aiSkillOptions, v));
  }

  return [];
};

/**
 * ListingCardImage
 * Component responsible for rendering the image part of the listing card.
 * It either renders the first image from the listing's images array with lazy loading,
 * or a stylized placeholder if images are disabled for the listing type.
 * Also wraps the image in a fixed aspect ratio container for consistent layout.
 * @component
 * @param {Object} props
 * @param {Object} props.listing listing entity with image data
 * @param {Function?} props.setActivePropsMaybe mouse enter/leave handlers for map highlighting
 * @param {string} props.title listing title for alt text
 * @param {string} props.renderSizes img/srcset size rules
 * @param {number} props.aspectWidth aspect ratio width
 * @param {number} props.aspectHeight aspect ratio height
 * @param {string} props.variantPrefix image variant prefix (e.g. "listing-card")
 * @param {boolean} props.showListingImage whether to show actual listing image or not
 * @param {Object?} props.style the background color for the listing card with no image
 * @returns {JSX.Element} listing image with fixed aspect ratio or fallback preview
 */
const ListingCardImage = props => {
  const {
    listing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    aspectRatioClassName,
    lazyLoadImage,
    isFreelancer,
  } = props;

  const firstImage = isFreelancer ? listing.author?.profileImage : listing?.images?.[0];
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const aspectRatioClass = aspectRatioClassName || css.aspectRatioWrapper;
  const ImageComponent = lazyLoadImage ? LazyImage : ResponsiveImage;

  return (
    <AspectRatioWrapper
      className={aspectRatioClass}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      <ImageComponent
        rootClassName={css.rootForImage}
        alt={title}
        image={firstImage}
        variants={variants}
        sizes={renderSizes}
      />
    </AspectRatioWrapper>
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.aspectRatioClassName custom className for AspectRatioWrapper component
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();
  const currentUserType = useSelector(currentUserTypeSelector);

  const {
    className,
    rootClassName,
    aspectRatioClassName,
    darkMode,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    lazyLoadImage = true,
  } = props;

  const translations = getListingCardTranslations(listing, config, intl);
  const {
    titlePlain,
    titleFormatted,
    titleFirstPartFormatted,
    cardAriaLabel,
    showPrice,
    priceTooltip,
    priceMessage,
    authorName,
  } = translations;

  const classes = classNames(rootClassName || css.root, className);

  const id = listing?.id?.uuid;
  const { title = '', publicData } = listing?.attributes || {};
  const slug = createSlug(title);

  const { listingType, cardStyle } = publicData || {};
  const listingFieldConfigs = config?.listing?.listingFields || [];
  const validListingTypes = config.listing.listingTypes || [];
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  // Render the listing image only if listing images are enabled in the listing type
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  // Sets the listing as active in the search map when hovered (if the search map is enabled)
  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(listing?.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const isFreelancer = listingType === 'freelancers';
  const freelancerDetailLines = isFreelancer
    ? getFreelancerDetailLines(listing, listingFieldConfigs, config)
    : [];

  const categoryLevel1Label = (() => {
    const categoryLevel1 = publicData?.categoryLevel1;
    const categoryLevel2 = publicData?.categoryLevel2;
    const categories = config?.categoryConfiguration?.categories || [];
    console.log('categoryLevel1', categoryLevel1);

    if (categoryLevel1 === 'content-creator') {
      return getCategoryName(
        categories.flatMap(c => c.subcategories || []),
        categoryLevel2
      );
    }

    return getCategoryName(categories, categoryLevel1);
  })();

  return (
    <NamedLink
      className={classes}
      name="ListingPage"
      params={{ id, slug }}
      ariaLabel={cardAriaLabel}
    >
      {showListingImage ? (
        <ListingCardImage
          renderSizes={renderSizes}
          title={titlePlain}
          listing={listing}
          setActivePropsMaybe={setActivePropsMaybe}
          aspectWidth={aspectWidth}
          aspectHeight={aspectHeight}
          variantPrefix={variantPrefix}
          aspectRatioClassName={aspectRatioClassName}
          lazyLoadImage={lazyLoadImage}
          isFreelancer={isFreelancer}
        />
      ) : (
        <ListingCardThumbnail
          style={cardStyle}
          listingTitle={title}
          className={aspectRatioClassName}
          width={aspectWidth}
          height={aspectHeight}
          setActivePropsMaybe={setActivePropsMaybe}
        />
      )}
      <div className={css.info}>
        {isFreelancer ? (
          <div className={css.mainInfo}>
            <div className={classNames(css.title, { [css.lightText]: darkMode })}>
              {titleFirstPartFormatted}
            </div>

            {categoryLevel1Label ? (
              <div className={css.categoryLevel1}>{categoryLevel1Label}</div>
            ) : null}

            {showPrice && currentUserType === 'customer' ? (
              <div className={css.freelancerPrice} title={priceTooltip}>
                {priceMessage}
              </div>
            ) : null}
            {/* {freelancerDetailLines.map((line, i) => (
              <div key={i} className={css.freelancerDetailLine}>
                {line}
              </div>
            ))} */}
          </div>
        ) : (
          <>
            {showPrice ? (
              <div className={css.price} title={priceTooltip}>
                {priceMessage}
              </div>
            ) : null}
            <div className={css.mainInfo}>
              {showListingImage && (
                <div className={classNames(css.title, { [css.lightText]: darkMode })}>
                  {titleFormatted}
                </div>
              )}
              {showAuthorInfo ? (
                <div className={classNames(css.authorInfo, { [css.lightText]: darkMode })}>
                  {authorName}
                </div>
              ) : null}
            </div>
          </>
        )}
      </div>
    </NamedLink>
  );
};

export default ListingCard;
